import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, History, Calculator, Thermometer, Copy, Check } from 'lucide-react';

interface HistoryEntry {
  id: number;
  from: string;
  to: string;
  timestamp: string;
}

interface UnitDefinition {
  category: string;
  symbol: string;
  fullName: string;
  description: string;
}

type UnitName = 'celsius' | 'fahrenheit' | 'kelvin' | 'rankine' | 'delisle' | 'newton' | 'reaumur' | 'romer' | 'gas_mark';

const TemperatureConverter: React.FC = () => {
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');
  const [fromUnit, setFromUnit] = useState<UnitName>('celsius');
  const [toUnit, setToUnit] = useState<UnitName>('fahrenheit');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [precision, setPrecision] = useState<number>(2);

  // Temperature unit definitions
  const units: Record<UnitName, UnitDefinition> = {
    'celsius': { 
      category: 'Common', 
      symbol: '°C', 
      fullName: 'Celsius',
      description: 'Water freezes at 0°C, boils at 100°C'
    },
    'fahrenheit': { 
      category: 'Common', 
      symbol: '°F', 
      fullName: 'Fahrenheit',
      description: 'Water freezes at 32°F, boils at 212°F'
    },
    'kelvin': { 
      category: 'Scientific', 
      symbol: 'K', 
      fullName: 'Kelvin',
      description: 'Absolute zero at 0K, SI base unit'
    },
    'rankine': { 
      category: 'Scientific', 
      symbol: '°R', 
      fullName: 'Rankine',
      description: 'Absolute Fahrenheit scale'
    },
    'delisle': { 
      category: 'Historical', 
      symbol: '°De', 
      fullName: 'Delisle',
      description: 'Water boils at 0°De, freezes at 150°De'
    },
    'newton': { 
      category: 'Historical', 
      symbol: '°N', 
      fullName: 'Newton',
      description: 'Water freezes at 0°N, boils at 33°N'
    },
    'reaumur': { 
      category: 'Historical', 
      symbol: '°Ré', 
      fullName: 'Réaumur',
      description: 'Water freezes at 0°Ré, boils at 80°Ré'
    },
    'romer': { 
      category: 'Historical', 
      symbol: '°Rø', 
      fullName: 'Rømer',
      description: 'Water freezes at 7.5°Rø, boils at 60°Rø'
    },
    'gas_mark': { 
      category: 'Cooking', 
      symbol: 'GM', 
      fullName: 'Gas Mark',
      description: 'UK oven temperature scale'
    }
  };

  const categories = Array.from(new Set(Object.values(units).map(unit => unit.category)));

  // Temperature conversion functions
  const convertToCelsius = (value: number, from: UnitName): number => {
    switch (from) {
      case 'celsius': return value;
      case 'fahrenheit': return (value - 32) * 5/9;
      case 'kelvin': return value - 273.15;
      case 'rankine': return (value - 491.67) * 5/9;
      case 'delisle': return 100 - value * 2/3;
      case 'newton': return value * 100/33;
      case 'reaumur': return value * 5/4;
      case 'romer': return (value - 7.5) * 40/21;
      case 'gas_mark': return value * 14 + 121; // Approximate conversion
      default: return value;
    }
  };

  const convertFromCelsius = (value: number, to: UnitName): number => {
    switch (to) {
      case 'celsius': return value;
      case 'fahrenheit': return value * 9/5 + 32;
      case 'kelvin': return value + 273.15;
      case 'rankine': return (value + 273.15) * 9/5;
      case 'delisle': return (100 - value) * 3/2;
      case 'newton': return value * 33/100;
      case 'reaumur': return value * 4/5;
      case 'romer': return value * 21/40 + 7.5;
      case 'gas_mark': return Math.max(0, (value - 121) / 14); // Approximate conversion
      default: return value;
    }
  };

  const convert = (value: number, from: UnitName, to: UnitName): number => {
    if (!value && value !== 0 || isNaN(value)) return 0;
    const celsiusValue = convertToCelsius(value, from);
    const result = convertFromCelsius(celsiusValue, to);
    return parseFloat(result.toFixed(precision));
  };

  const handleFromValueChange = (value: string): void => {
    setFromValue(value);
    if ((value || value === '0') && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), fromUnit, toUnit);
      setToValue(converted.toString());
    } else {
      setToValue('');
    }
  };

  const handleToValueChange = (value: string): void => {
    setToValue(value);
    if ((value || value === '0') && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), toUnit, fromUnit);
      setFromValue(converted.toString());
    } else {
      setFromValue('');
    }
  };

  const swapUnits = (): void => {
    const tempUnit = fromUnit;
    const tempValue = fromValue;
    
    setFromUnit(toUnit);
    setToUnit(tempUnit);
    setFromValue(toValue);
    setToValue(tempValue);
  };

  const addToHistory = (): void => {
    if ((fromValue || fromValue === '0') && (toValue || toValue === '0')) {
      const entry: HistoryEntry = {
        id: Date.now(),
        from: `${fromValue}${units[fromUnit].symbol}`,
        to: `${toValue}${units[toUnit].symbol}`,
        timestamp: new Date().toLocaleTimeString()
      };
      setHistory(prev => [entry, ...prev.slice(0, 9)]); // Keep last 10 entries
    }
  };

  const copyResult = (): void => {
    if (toValue || toValue === '0') {
      navigator.clipboard.writeText(`${toValue}${units[toUnit].symbol}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = (): void => {
    setFromValue('');
    setToValue('');
  };

  const getTemperatureColor = (temp: number, unit: UnitName): string => {
    const celsius = convertToCelsius(temp, unit);
    if (celsius < 0) return 'text-blue-600';
    if (celsius < 10) return 'text-blue-500';
    if (celsius < 20) return 'text-green-600';
    if (celsius < 30) return 'text-yellow-600';
    if (celsius < 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getCommonReferences = (temp: number, unit: UnitName): string[] => {
    const celsius = convertToCelsius(temp, unit);
    const references: string[] = [];
    
    if (Math.abs(celsius - 0) < 1) references.push('Water freezes');
    if (Math.abs(celsius - 100) < 1) references.push('Water boils');
    if (Math.abs(celsius - 37) < 1) references.push('Human body temperature');
    if (Math.abs(celsius - (-273.15)) < 1) references.push('Absolute zero');
    if (celsius >= 20 && celsius <= 25) references.push('Room temperature');
    
    return references;
  };

  useEffect(() => {
    if ((fromValue || fromValue === '0') && !isNaN(Number(fromValue))) {
      const converted = convert(parseFloat(fromValue), fromUnit, toUnit);
      setToValue(converted.toString());
    }
  }, [fromUnit, toUnit, precision, fromValue]);

  const renderUnitOptions = (): React.ReactElement[] => {
    return categories.map(category => (
      <optgroup key={category} label={category}>
        {Object.entries(units)
          .filter(([_, unit]) => unit.category === category)
          .map(([unitName, unit]) => (
            <option key={unitName} value={unitName}>
              {unit.fullName} ({unit.symbol})
            </option>
          ))}
      </optgroup>
    ));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-orange-50 to-red-100 min-h-screen">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Thermometer className="w-8 h-8 text-orange-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">Advanced Temperature Converter</h1>
          </div>
          <p className="text-gray-600">Convert between multiple temperature scales with precision</p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 mb-6 justify-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Precision:</label>
            <select 
              value={precision} 
              onChange={(e) => setPrecision(parseInt(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              {[0, 1, 2, 3, 4, 5, 6].map(p => (
                <option key={p} value={p}>{p} decimal{p !== 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <History className="w-4 h-4" />
            History
          </button>
          
          <button
            onClick={clearAll}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            Clear All
          </button>
        </div>

        {/* Main Converter */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* From Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              From
            </h3>
            <div className="space-y-3">
              <input
                type="number"
                value={fromValue}
                onChange={(e) => handleFromValueChange(e.target.value)}
                placeholder="Enter temperature"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {units[fromUnit].description}
              </div>
            </div>
          </div>

          {/* To Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              To
            </h3>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="number"
                  value={toValue}
                  onChange={(e) => handleToValueChange(e.target.value)}
                  placeholder="Result"
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent pr-12"
                />
                <button
                  onClick={copyResult}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Copy result"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                </button>
              </div>
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {units[toUnit].description}
              </div>
            </div>
          </div>
        </div>

        {/* Swap Button */}
        <div className="flex justify-center mb-6">
          <button
            onClick={swapUnits}
            className="flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors shadow-md"
          >
            <ArrowLeftRight className="w-5 h-5" />
            Swap Units
          </button>
        </div>

        {/* Add to History Button */}
        {(fromValue || fromValue === '0') && (toValue || toValue === '0') && (
          <div className="flex justify-center mb-6">
            <button
              onClick={addToHistory}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              Add to History
            </button>
          </div>
        )}

        {/* Conversion Summary */}
        {(fromValue || fromValue === '0') && (toValue || toValue === '0') && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Conversion Summary</h4>
            <p className="text-lg mb-2">
              <span className={`font-mono bg-white px-2 py-1 rounded ${getTemperatureColor(parseFloat(fromValue), fromUnit)}`}>
                {fromValue}{units[fromUnit].symbol}
              </span>
              <span className="mx-3 text-gray-500">=</span>
              <span className={`font-mono bg-white px-2 py-1 rounded font-semibold ${getTemperatureColor(parseFloat(toValue), toUnit)}`}>
                {toValue}{units[toUnit].symbol}
              </span>
            </p>
            
            {/* Temperature References */}
            {fromValue && !isNaN(Number(fromValue)) && (
              <div className="text-sm text-gray-600">
                {getCommonReferences(parseFloat(fromValue), fromUnit).map(ref => (
                  <span key={ref} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2 mb-1">
                    {ref}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Reference Table */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-gray-800 mb-3">Quick Reference</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="space-y-2">
              <h5 className="font-medium text-blue-800">Water Freezing</h5>
              <div>0°C = 32°F</div>
              <div>273.15K = 491.67°R</div>
            </div>
            <div className="space-y-2">
              <h5 className="font-medium text-red-800">Water Boiling</h5>
              <div>100°C = 212°F</div>
              <div>373.15K = 671.67°R</div>
            </div>
            <div className="space-y-2">
              <h5 className="font-medium text-green-800">Room Temperature</h5>
              <div>20°C = 68°F</div>
              <div>293.15K = 527.67°R</div>
            </div>
            <div className="space-y-2">
              <h5 className="font-medium text-purple-800">Body Temperature</h5>
              <div>37°C = 98.6°F</div>
              <div>310.15K = 558.27°R</div>
            </div>
          </div>
        </div>

        {/* History */}
        {showHistory && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <History className="w-5 h-5" />
              Conversion History
            </h4>
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No conversions in history</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((entry) => (
                  <div key={entry.id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">{entry.from}</span>
                      <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-sm font-semibold">{entry.to}</span>
                    </div>
                    <span className="text-xs text-gray-500">{entry.timestamp}</span>
                  </div>
                ))}
              </div>
            )}
            {history.length > 0 && (
              <button
                onClick={() => setHistory([])}
                className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-sm"
              >
                Clear History
              </button>
            )}
          </div>
        )}

        {/* Unit Categories Info */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map(category => (
            <div key={category} className="bg-gray-50 rounded-lg p-4">
              <h5 className="font-semibold text-gray-800 mb-2">{category}</h5>
              <div className="space-y-1">
                {Object.entries(units)
                  .filter(([_, unit]) => unit.category === category)
                  .map(([unitName, unit]) => (
                    <div key={unitName} className="text-sm text-gray-600">
                      {unit.symbol} - {unit.fullName}
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TemperatureConverter;